import { useMemo, useRef, useState } from "react";
import { Check, ChevronRight, Hotel, LogOut, MapPin } from "lucide-react";
import { BrandMark, Icon, useMenuDismiss } from "@cntrl/ui";
import { useMe } from "../../app/hooks/useSession.js";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import { usePartnerShow, usePartnerView, type PartnerViewItem } from "../../app/hooks/usePartner.js";
import { usePartnerLogout } from "../../shells/partner/PartnerShell.js";
import { PartnerProfileModal } from "./PartnerProfileModal.js";
import {
  eventLabel,
  eventTimeLabel,
  localIso,
  monthDay,
  partnerFlights,
  relativeChip,
  rowModel,
  shortDate,
  stayingLabel,
  t12,
  tspan,
  upcoming,
} from "./partnerView.js";
import { JourneyStrip } from "../mobile/JourneyStrip.js";
import styles from "./PartnerScreen.module.scss";
import { BandAvatar } from "../shared/BandAvatar.js";

export interface PartnerScreenProps {
  /** Open a show's detail (a route, so the back swipe and browser Back work). */
  onOpenShow: (showId: string) => void;
  /** Open an event's detail (a rehearsal, a session…). */
  onOpenEvent: (eventId: string) => void;
}

/**
 * The partner view (ART-239 part 2; the UI says "partner", the code keeps "partner") — a read-only window onto ONE
 * member's logistics: the dates they are on across every band they are in, where they are staying, and whether THE PARTNER is on
 * the list. Built to the spec's own markup (sources/ART-239-AC-Partner-View-Dev-Reference.html, `m-partner.css`).
 *
 * Everything on it comes from `GET /me/partner/view` (one read, the partner's guest status inline) plus the next show's detail
 * for its load-in and lodging. Times are 12-hour everywhere. The member drives everything; the partner's only action is
 * signing out.
 */
export function PartnerScreen({ onOpenShow, onOpenEvent }: PartnerScreenProps) {
  const view = usePartnerView();
  const me = useMe();
  const firstName = me.data?.person?.firstName?.trim() || null;

  if (view.isLoading) {
    return (
      <div className={styles.app}>
        <p className={styles.state} role="status">Loading…</p>
      </div>
    );
  }
  if (view.isError || !view.data) {
    return (
      <div className={styles.app}>
        <p className={styles.state} role="alert">Couldn&rsquo;t load right now.</p>
      </div>
    );
  }

  const data = view.data;
  const member = data.member;
  const items = upcoming(data.items);
  const multiBand = (data.bandCount ?? 0) > 1;
  const next = items[0];

  return (
    <div className={styles.app}>
      <TopBar
        firstName={firstName}
        name={me.data?.person?.displayName ?? firstName}
        email={me.data?.person?.email}
        personId={me.data?.person?.personId}
        avatarMediaId={me.data?.person?.avatarMediaId}
      />
      <div className={styles.body}>
        {member ? (
          <KeepCard name={member.name ?? "Your partner"} personId={member.personId} avatarMediaId={member.avatarMediaId} />
        ) : (
          // The link is gone (removed by the member) — say so rather than pretending to load.
          <p className={styles.state}>You&rsquo;re no longer connected to anyone&rsquo;s schedule.</p>
        )}
        {member && !next && (
          <div className={styles.empty}>
            <h2 className={styles.emptyTitle}>All quiet</h2>
            <p className={styles.emptyText}>No shows on the calendar yet, they&rsquo;ll appear here automatically.</p>
          </div>
        )}
        {member && next && (
          <>
            <Hero item={next} memberFirstName={member.firstName} onOpenShow={onOpenShow} onOpenEvent={onOpenEvent} />
            {next.kind === "show" && next.onList && (
              <div className={styles.gpill}>
                <span className={styles.gpillIc} aria-hidden="true"><Icon icon={Check} size={16} /></span>
                <span>
                  <span className={styles.gpillT}>You&rsquo;re on the list</span>
                  <span className={styles.gpillS}>
                    {next.title ?? "Show"}
                    {(next.plusCount ?? 0) > 0 ? ` · +${next.plusCount} guest${next.plusCount === 1 ? "" : "s"}` : ""}
                  </span>
                </span>
              </div>
            )}
            {items.length > 1 && (
              <>
                <h2 className={styles.seclabel}>On the calendar</h2>
                <ul className={styles.rows}>
                  {items.slice(1).map((item) => (
                    <CalendarRow key={`${item.kind}-${item.id}`} item={item} multiBand={multiBand}
                      onOpenShow={onOpenShow} onOpenEvent={onOpenEvent} />
                  ))}
                </ul>
              </>
            )}
          </>
        )}
        {member && <p className={styles.foot}>{member.name}&rsquo;s logistics only · read-only</p>}
      </div>
    </div>
  );
}

// ---- top bar ---------------------------------------------------------------------------------------------

function TopBar({
  firstName,
  name,
  email,
  personId,
  avatarMediaId,
}: {
  firstName: string | null;
  name: string | null | undefined;
  email?: string | null;
  personId?: string | null;
  avatarMediaId?: string | null;
}) {
  const logout = usePartnerLogout();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  // Their own picture (ART-239 "Edit my profile"); the initial stands in until they set one.
  const photo = useAvatarUrl("person", personId, avatarMediaId);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useMenuDismiss(open, () => setOpen(false), rootRef, triggerRef);
  const initial = (firstName ?? name ?? "?").charAt(0).toUpperCase();
  const today = new Date()
    .toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
    .replace(",", " ·")
    .toUpperCase();

  return (
    <header className={styles.top}>
      <div>
        <div className={styles.topDate}>{today}</div>
        <div className={styles.topHiRow}>
          <BrandMark size={27} decorative />
          <h1 className={styles.topHi}>Hi, {firstName ?? "there"}</h1>
        </div>
      </div>
      <div className={styles.profWrap} ref={rootRef}>
        <button
          ref={triggerRef}
          type="button"
          className={styles.prof}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Your account"
          onClick={() => setOpen((v) => !v)}
        >
          <span className={styles.profAv} aria-hidden="true">
            {photo ? <img className={styles.profImg} src={photo} alt="" /> : initial}
          </span>
        </button>
        {open && (
          <div className={styles.profMenu} role="menu" aria-label="Your account">
            <div className={styles.profMenuHd}>
              <span className={styles.profMenuAv} aria-hidden="true">
                {photo ? <img className={styles.profImg} src={photo} alt="" /> : initial}
              </span>
              <span className={styles.profMenuId}>
                <span className={styles.profMenuNm}>{name ?? firstName ?? ""}</span>
                {email && <span className={styles.profMenuEm}>{email}</span>}
                <button
                  type="button"
                  role="menuitem"
                  className={styles.profMenuEdit}
                  onClick={() => {
                    setOpen(false);
                    setEditing(true);
                  }}
                >
                  Edit my profile
                </button>
              </span>
            </div>
            <button type="button" role="menuitem" className={styles.profMenuOut} onClick={() => { setOpen(false); logout(); }}>
              <Icon icon={LogOut} size={20} /> Sign out
            </button>
          </div>
        )}
      </div>
      {editing && <PartnerProfileModal onClose={() => setEditing(false)} />}
    </header>
  );
}

// ---- keep-up card ----------------------------------------------------------------------------------------

function KeepCard({ name, personId, avatarMediaId }: { name: string; personId?: string | null; avatarMediaId?: string | null }) {
  // The member's picture rides the person avatar route, which serves it only to viewers who may see their profile — a
  // partner usually may not, and then the tile falls back to the member's initial on crimson (the spec's fallback).
  const photo = useAvatarUrl("person", personId, avatarMediaId);
  return (
    <div className={styles.keep}>
      <span className={styles.av} aria-hidden="true">
        {photo ? <img src={photo} alt="" /> : name.charAt(0).toUpperCase()}
      </span>
      <span>
        <span className={styles.keepK}>Keep up with</span>
        <span className={styles.keepN}>{name}</span>
      </span>
    </div>
  );
}

// ---- hero ------------------------------------------------------------------------------------------------

function Hero({
  item,
  memberFirstName,
  onOpenShow,
  onOpenEvent,
}: {
  item: PartnerViewItem;
  memberFirstName?: string | null;
  onOpenShow: (id: string) => void;
  onOpenEvent: (id: string) => void;
}) {
  const date = item.date ?? localIso();
  const head = (
    <div className={styles.heroTop}>
      <span className={styles.heroK}>Next up</span>
      <span className={styles.heroIn}>{relativeChip(date)}</span>
    </div>
  );
  const band = (
    <div className={styles.heroArtist}>
      {/* The band's photo (ART-239 — "missing the Whitsett photo"); the initial stands in until it loads or when
          the band has none. A band photo is public identity, so the seatless partner can fetch it. */}
      <BandAvatar
        bandId={item.bandId}
        avatarMediaId={item.bandAvatarMediaId}
        initials={(item.bandName ?? "?").charAt(0).toUpperCase()}
        size="xs"
        shape="rounded"
      />
      <span className={styles.heroAname}>{item.bandName}</span>
    </div>
  );

  if (item.kind === "show") return <ShowHero item={item} memberFirstName={memberFirstName} head={head} band={band} onOpen={onOpenShow} />;

  if (item.kind === "travel" || item.kind === "off") {
    const travel = item.kind === "travel";
    return (
      <section className={styles.hero} aria-label="Next up">
        {head}
        <h2 className={styles.heroVenue}><span className={styles.offDot} aria-hidden="true" />{travel ? "Travel day" : "Off day"}</h2>
        <p className={styles.heroSub}>
          {travel ? [item.fromCity, item.toCity].filter(Boolean).join(" → ") || "On the road" : item.city ? `Day off in ${item.city}` : "Day off"}
        </p>
        <p className={styles.heroMeta}>
          <Icon icon={MapPin} size={16} /> {travel ? "On the road" : "Rest day"} · {shortDate(date)}
        </p>
        {band}
        {travel && (item.departTime || item.busCall) && (
          <div className={styles.stats}>
            {item.departTime && <Stat k="Rolls" v={t12(item.departTime)} />}
            {item.busCall && <Stat k="Bus call" v={item.busCall} />}
          </div>
        )}
      </section>
    );
  }

  // an event: rehearsal, session, shoot…
  const when = tspan(item.startTime, item.endTime);
  return (
    <section className={styles.hero} aria-label="Next up">
      {head}
      <h2 className={styles.heroVenue}>{eventLabel(item.eventType)}</h2>
      {item.title && <p className={styles.heroSub}>{item.title}</p>}
      <p className={styles.heroMeta}>
        <Icon icon={MapPin} size={16} /> {[item.locationName, shortDate(date)].filter(Boolean).join(" · ")}
      </p>
      {band}
      {when && (
        <div className={styles.stats}>
          <Stat k={eventTimeLabel(item.eventType)} v={when} />
        </div>
      )}
      <button type="button" className={styles.dosLink} onClick={() => item.id && onOpenEvent(item.id)}>
        Details <Icon icon={ChevronRight} size={16} />
      </button>
    </section>
  );
}

function ShowHero({
  item,
  memberFirstName,
  head,
  band,
  onOpen,
}: {
  item: PartnerViewItem;
  memberFirstName?: string | null;
  head: React.ReactNode;
  band: React.ReactNode;
  onOpen: (id: string) => void;
}) {
  // The next show's detail carries the member's band's load-in and their lodging; the list does not.
  const detail = usePartnerShow(item.id ?? undefined);
  const hotel = detail.data?.travel?.find((t) => t.kind === "hotel");
  const flights = partnerFlights(detail.data?.travel);
  const place = [item.city, item.region].filter(Boolean).join(", ");
  // Showtime is THE MEMBER's set; with no set time the doors time is shown under its own name — never doors labelled
  // "Showtime" (the spec's own gap).
  const show = item.startTime ? { k: "Showtime", v: t12(item.startTime) } : item.doorsTime ? { k: "Doors", v: t12(item.doorsTime) } : null;
  const loadIn = detail.data?.loadIn ? t12(detail.data.loadIn) : null;

  return (
    <section className={styles.hero} aria-label="Next up">
      {head}
      <h2 className={styles.heroVenue}>{item.title ?? "Show"}</h2>
      <p className={styles.heroMeta}>
        <Icon icon={MapPin} size={16} /> {[place, shortDate(item.date ?? localIso())].filter(Boolean).join(" · ")}
        {item.confirmed === false ? " · not locked in yet" : ""}
      </p>
      {band}
      {(loadIn || show) && (
        <div className={styles.stats}>
          {loadIn && <Stat k="Load-in" v={loadIn} />}
          {show && <Stat k={show.k} v={show.v} />}
        </div>
      )}
      <div className={styles.heroHotel}>
        <Icon icon={Hotel} size={20} />
        <span>
          <span className={styles.heroHk}>{stayingLabel(memberFirstName)}</span>
          <span className={styles.heroHv}>{hotel?.propertyName ?? "Not set yet"}</span>
        </span>
      </div>
      {flights.legs.length > 0 && (
        // The crew's live flight strip (ART-239), in its LIGHT tone: a mini row on this card like the hotel's above,
        // not a dark card inside it (PO).
        <div className={styles.heroFlight} aria-label="Flights">
          <JourneyStrip legs={flights.legs} statusById={flights.statusById} tone="light" />
        </div>
      )}
      <button type="button" className={styles.dos} onClick={() => item.id && onOpen(item.id)}>
        View day of show <Icon icon={ChevronRight} size={16} />
      </button>
    </section>
  );
}

function Stat({ k, v }: { k: string; v: string }) {
  return (
    <div className={styles.stat}>
      <div className={styles.statK}>{k}</div>
      <div className={styles.statV}>{v}</div>
    </div>
  );
}

// ---- calendar row ----------------------------------------------------------------------------------------

function CalendarRow({
  item,
  multiBand,
  onOpenShow,
  onOpenEvent,
}: {
  item: PartnerViewItem;
  multiBand: boolean;
  onOpenShow: (id: string) => void;
  onOpenEvent: (id: string) => void;
}) {
  const m = useMemo(() => rowModel(item, multiBand), [item, multiBand]);
  const d = monthDay(item.date ?? localIso());
  const off = item.kind === "off" || item.kind === "travel";
  const content = (
    <>
      <span className={styles.rowD} aria-hidden="true">
        <span className={styles.rowMon}>{d.mon}</span>
        <span className={styles.rowDay}>{d.day}</span>
      </span>
      <span className={styles.rowB}>
        <span className={styles.rowV}>
          {m.route ? <>{m.route.from}<span className={styles.routeAr}>→</span>{m.route.to}</> : m.title}
        </span>
        {m.sub && <span className={styles.rowC}>{m.sub}</span>}
      </span>
      <span className={styles.chip} data-tone={m.tone}>{m.chip}</span>
    </>
  );
  // Off and travel days carry everything on the row and open nothing — so they are not buttons (the spec's
  // "looks tappable but isn't" gap).
  return (
    <li>
      {off ? (
        <div className={styles.row} data-off="true">{content}</div>
      ) : (
        <button
          type="button"
          className={styles.row}
          aria-label={`${shortDate(item.date ?? localIso())}: ${m.title}`}
          onClick={() => item.id && (item.kind === "show" ? onOpenShow(item.id) : onOpenEvent(item.id))}
        >
          {content}
        </button>
      )}
    </li>
  );
}
